import React from 'react';
import { DateRangeFilter as DateRangeFilterType } from '../types';
import { useFilter } from '../FilterContext';
import { cn } from '../../../../lib/utils';

import { useTranslation } from 'react-i18next';

interface DateRangeFilterProps {
  filter: DateRangeFilterType;
  className?: string;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  filter,
  className,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  const { value: dateRangeValue, update, isActive } = useFilter(filter.id);
  const value = dateRangeValue as { start: Date | null; end: Date | null };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const startDate = e.target.value ? new Date(e.target.value) : null;
    update({
      start: startDate,
      end: value?.end || null,
    });
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const endDate = e.target.value ? new Date(e.target.value) : null;
    update({
      start: value?.start || null,
      end: endDate,
    });
  };

  const formatDateForInput = (date: Date | null): string => {
    if (!date) {
      return '';
    }
    return date.toISOString().split('T')[0];
  };

  return (
    <div className={cn('space-y-2', className)}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {filter.label}
        {filter.required && <span className="ml-1 text-red-500">*</span>}
      </label>
      <div className="flex items-center space-x-2">
        <div className="flex-1">
          <input
            type="date"
            value={formatDateForInput(value?.start || null)}
            onChange={handleStartDateChange}
            min={
              filter.minDate ? formatDateForInput(filter.minDate) : undefined
            }
            max={
              filter.maxDate ? formatDateForInput(filter.maxDate) : undefined
            }
            placeholder={t('startDate')}
            className={cn(
              'w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm dark:border-gray-600',
              'focus:border-[#FF732E] focus:ring-2 focus:ring-[#FF732E] focus:outline-none',
              'text-sm',
              isActive && 'border-[#FF732E] bg-orange-50 dark:bg-gray-800',
            )}
          />
        </div>

        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('to')}
        </span>

        <div className="flex-1">
          <input
            type="date"
            value={formatDateForInput(value?.end || null)}
            onChange={handleEndDateChange}
            min={
              filter.minDate ? formatDateForInput(filter.minDate) : undefined
            }
            max={
              filter.maxDate ? formatDateForInput(filter.maxDate) : undefined
            }
            placeholder={t('endDate')}
            className={cn(
              'w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm dark:border-gray-600',
              'focus:border-[#FF732E] focus:ring-2 focus:ring-[#FF732E] focus:outline-none',
              'text-sm',
              isActive && 'border-[#FF732E] bg-orange-50 dark:bg-gray-800',
            )}
          />
        </div>
      </div>
      {filter.placeholder && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {filter.placeholder}
        </p>
      )}
    </div>
  );
};
